"use client"

import {
  useArtifactCanvas,
  useFocusStore,
  navigateWithThreadPreservation,
  PersonListCard,
  ListCardTrailingActionsFooter,
  Badge,
  Button,
  formatBriefDate,
  briefCandidateToPersonModel,
  talentLocationLine,
  talentTopSkills,
  cn,
} from '@yeshuman/sdk'
import { useCallback, useRef, type MouseEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import type { BriefCandidate } from '../../hooks/use-briefs'
import { useSendCandidateToInterview } from '../../hooks/use-briefs'
import { consumePendingFocusPath, stashPendingFocusPath } from '../../lib/focus-handoff'
import { interviewOutcomeBadgeVariant } from '../../lib/interview-outcome-display'
import { BriefCandidateOutcomeControl } from './brief-candidate-outcome-control'
import { ScorecardSummaryLine } from '../interview/scorecard-summary'
import { TalentProfilePreviewCanvas } from './talent-profile-preview-canvas'
import { Calendar, CalendarCheck, MapPin, Send } from 'lucide-react'

export const INTERVIEWER_QUEUE_PATH = '/interviewer/candidates'
const INTERVIEWER_FOCUS = 'interviewer'

export type BriefSavedCandidateRowProps = {
  candidate: BriefCandidate
  /** Brief that owns this saved candidate; enables the footer actions (interview handoff, outcome). */
  briefId?: string
  /** Job linked to the brief; outcome actions stay disabled until one exists. */
  briefOpportunityId?: string | null
  briefOpportunityTitle?: string | null
  className?: string
  variant?: 'default' | 'compact'
}

/** Saved talent on brief detail — composes {@link PersonListCard} with brief-specific slots. */
export function BriefSavedCandidateRow({
  candidate,
  briefId,
  briefOpportunityId,
  briefOpportunityTitle,
  className,
  variant = 'default',
}: BriefSavedCandidateRowProps) {
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { sendToInterview, isSending } = useSendCandidateToInterview()
  const { focus, setFocus } = useFocusStore()
  const previewOpenKeyRef = useRef(0)
  const preview = candidate.profile_preview
  const personModel = briefCandidateToPersonModel(candidate)
  const displayName = personModel.name
  const locationLine = talentLocationLine(preview)
  const skills = talentTopSkills(preview, variant === 'compact' ? 3 : 5)
  const notes = candidate.notes?.trim()
  const isCompact = variant === 'compact'
  const inInterview = candidate.in_interview === true
  const outcome = candidate.outcome ?? null
  const outcomeNotes = outcome?.notes?.trim()
  const scorecard = candidate.scorecard ?? null

  const openProfilePreview = useCallback(() => {
    previewOpenKeyRef.current += 1
    openCanvas({
      title: displayName,
      preventAutoFocus: true,
      content: (
        <TalentProfilePreviewCanvas
          key={`brief-saved-${candidate.profile_id}-${previewOpenKeyRef.current}`}
          profileId={candidate.profile_id}
          profileName={displayName}
          onClose={closeCanvas}
        />
      ),
    })
  }, [candidate.profile_id, closeCanvas, displayName, openCanvas])

  const goToInterviewerQueue = useCallback(() => {
    navigateWithThreadPreservation(navigate, INTERVIEWER_QUEUE_PATH, searchParams)
  }, [navigate, searchParams])

  // UI only mounts /interviewer/* while the user's focus is `interviewer`, and switching
  // focus lands on its dashboard; stash the queue path so the interviewer index route
  // forwards there (see lib/focus-handoff.ts).
  const openInterviewerQueue = useCallback(() => {
    const currentFocus = focus?.current_focus ?? null
    const canSwitch = focus?.available_foci?.includes(INTERVIEWER_FOCUS) ?? false
    if (currentFocus === INTERVIEWER_FOCUS || !canSwitch) {
      goToInterviewerQueue()
      return
    }
    stashPendingFocusPath(INTERVIEWER_FOCUS, INTERVIEWER_QUEUE_PATH)
    void setFocus(INTERVIEWER_FOCUS)
      .then((data: unknown) => {
        if (data) return
        // No focus change was applied (no token or superseded request); let the router decide.
        consumePendingFocusPath(INTERVIEWER_FOCUS)
        goToInterviewerQueue()
      })
      .catch((error: unknown) => {
        consumePendingFocusPath(INTERVIEWER_FOCUS)
        toast.error(
          error instanceof Error && error.message
            ? error.message
            : 'Could not open the interviewer queue',
        )
      })
  }, [focus?.available_foci, focus?.current_focus, goToInterviewerQueue, setFocus])

  const handleOpenQueue = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      openInterviewerQueue()
    },
    [openInterviewerQueue],
  )

  const handleSendToInterview = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      if (!briefId || isSending) return
      sendToInterview(
        { briefId, profileId: candidate.profile_id },
        {
          onSuccess: (result) => {
            toast.success(
              result.created
                ? `${displayName} sent to interview`
                : `${displayName} is in the interview queue`,
              {
                action: { label: 'Open queue', onClick: openInterviewerQueue },
              },
            )
          },
          onError: (error: Error) => {
            toast.error(error.message || 'Failed to send candidate to interview')
          },
        },
      )
    },
    [briefId, candidate.profile_id, displayName, isSending, openInterviewerQueue, sendToInterview],
  )

  const body = (
    <div className="pointer-events-none space-y-2">
      {locationLine ? (
        <div
          className={cn(
            'flex items-center gap-2 text-sm text-muted-foreground',
            isCompact && 'text-xs',
          )}
        >
          <MapPin className="size-3.5 shrink-0" aria-hidden />
          <span>{locationLine}</span>
        </div>
      ) : null}
      {skills.length > 0 ? (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {skills.map((skill) => (
            <Badge key={skill} variant="secondarySoft" className="text-xs font-normal">
              {skill}
            </Badge>
          ))}
        </div>
      ) : null}
      {notes ? (
        <p
          className={cn(
            'line-clamp-3 rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-sm text-muted-foreground',
            isCompact && 'text-xs',
          )}
        >
          {notes}
        </p>
      ) : null}
      {outcome ? (
        <div
          className={cn('space-y-1 pt-1', isCompact && 'text-xs')}
          data-testid="brief-candidate-outcome"
        >
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Badge variant={interviewOutcomeBadgeVariant(outcome.state)} className="text-xs font-medium">
              {outcome.state_label}
            </Badge>
            <span>{formatBriefDate(outcome.updated_at)}</span>
          </div>
          {outcomeNotes ? (
            <p className="line-clamp-2 text-sm text-muted-foreground">{outcomeNotes}</p>
          ) : null}
        </div>
      ) : null}
      {scorecard ? (
        <ScorecardSummaryLine
          recommendation={scorecard.recommendation}
          recommendationLabel={scorecard.recommendation_label}
          interviewerName={scorecard.interviewer_name}
          metTarget={scorecard.met_target}
          rated={scorecard.rated}
        />
      ) : null}
    </div>
  )

  const footer = briefId ? (
    <ListCardTrailingActionsFooter>
      <BriefCandidateOutcomeControl
        briefId={briefId}
        profileId={candidate.profile_id}
        candidateName={displayName}
        jobTitle={briefOpportunityTitle}
        hasLinkedJob={Boolean(briefOpportunityId)}
        outcome={outcome}
      />
      {inInterview ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={handleOpenQueue}
          data-testid="brief-candidate-open-interview-queue"
        >
          <CalendarCheck className="mr-1.5 size-4 shrink-0" aria-hidden />
          Open interview queue
        </Button>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="default"
          onClick={handleSendToInterview}
          disabled={isSending}
          data-testid="brief-candidate-send-to-interview"
        >
          <Send className="mr-1.5 size-4 shrink-0" aria-hidden />
          {isSending ? 'Sending…' : 'Send to interview'}
        </Button>
      )}
    </ListCardTrailingActionsFooter>
  ) : undefined

  return (
    <PersonListCard
      variant={isCompact ? 'compact' : 'standard'}
      className={cn('h-full', className)}
      person={personModel}
      titleBadge={
        inInterview
          ? { label: 'In interview', variant: 'default' }
          : { label: 'Saved', variant: 'secondarySoft' }
      }
      headerAside={
        <Badge variant="outline" className="shrink-0 text-xs font-normal">
          <Calendar className="mr-1 size-3" aria-hidden />
          {formatBriefDate(candidate.saved_at)}
        </Badge>
      }
      body={body}
      footer={footer}
      footerClassName={footer ? 'pointer-events-auto px-0' : undefined}
      onOpen={openProfilePreview}
      openAriaLabel={`View saved profile for ${displayName}`}
    />
  )
}
